import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { tokenService } from '../services';
import { authService } from '../services/auth.service';
import { tenantService } from '../services/tenant.service';
import type { AccessTokenPayload, ActiveProfile, ProfileSummary } from '../types/auth.types';
import type { ActiveTenant, TenantMembership } from '../types/tenant.types';

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  /** When true, user must select a profile before proceeding */
  requiresProfileSelection: boolean;
  /** Available profiles when selection is required */
  pendingProfiles: ProfileSummary[];
  /** Currently active profile */
  activeProfile: ActiveProfile | null;
  /** When true, user must select a tenant/company before proceeding */
  requiresTenantSelection: boolean;
  /** Available tenants when selection is required */
  pendingTenants: TenantMembership[];
  /** Currently active tenant context */
  activeTenant: ActiveTenant | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  selectProfile: (profileId: string) => Promise<void>;
  selectTenant: (tenantId: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [requiresProfileSelection, setRequiresProfileSelection] = useState(false);
  const [pendingProfiles, setPendingProfiles] = useState<ProfileSummary[]>([]);
  const [activeProfile, setActiveProfile] = useState<ActiveProfile | null>(null);
  const [requiresTenantSelection, setRequiresTenantSelection] = useState(false);
  const [pendingTenants, setPendingTenants] = useState<TenantMembership[]>([]);
  const [activeTenant, setActiveTenant] = useState<ActiveTenant | null>(null);

  /**
   * Parse tenant claims from the JWT token.
   * - If `custom:tenantId` is present → single tenant, auto-select.
   * - If `custom:tenantMemberships` is present → multiple tenants, require selection.
   */
  const parseTenantClaims = (payload: Record<string, unknown>) => {
    const tenantId = payload['custom:tenantId'] as string | undefined;
    const tenantMembershipsRaw = payload['custom:tenantMemberships'] as string | undefined;
    const tenantRoles = payload['custom:tenantRoles'] as string | undefined;

    if (tenantId) {
      // Single tenant — auto-select
      const roles = tenantRoles ? JSON.parse(tenantRoles) as string[] : [];
      const tenant: ActiveTenant = { tenantId, roles };
      setActiveTenant(tenant);
      sessionStorage.setItem('activeTenant', JSON.stringify(tenant));
      setRequiresTenantSelection(false);
      setPendingTenants([]);
      sessionStorage.removeItem('multiTenantUser');
    } else if (tenantMembershipsRaw) {
      // Multiple tenants — require selection
      try {
        const memberships = JSON.parse(tenantMembershipsRaw) as TenantMembership[];
        if (memberships.length === 1) {
          // Only one membership despite being in the list — auto-select
          const tenant: ActiveTenant = {
            tenantId: memberships[0].tenantId,
            companyName: memberships[0].companyName,
            roles: memberships[0].roles,
          };
          setActiveTenant(tenant);
          sessionStorage.setItem('activeTenant', JSON.stringify(tenant));
          setRequiresTenantSelection(false);
          setPendingTenants([]);
          sessionStorage.removeItem('multiTenantUser');
        } else if (memberships.length > 1) {
          setRequiresTenantSelection(true);
          setPendingTenants(memberships);
          setActiveTenant(null);
          sessionStorage.removeItem('activeTenant');
          sessionStorage.setItem('multiTenantUser', 'true');
          // Persist memberships so "Switch Tenant" can restore the list
          sessionStorage.setItem('tenantMemberships', JSON.stringify(memberships));
        }
      } catch {
        // If parsing fails, skip tenant selection
        console.error('Failed to parse tenantMemberships claim');
      }
    }
    // If neither claim is present, no tenant context — allow proceeding (backward compat)
  };

  useEffect(() => {
    // Check for existing session on mount
    const checkAuth = async () => {
      try {
        const token = tokenService.getAccessToken();
        if (token && !tokenService.isTokenExpired(token)) {
          const decoded = tokenService.decodeToken(token);
          const payload = decoded.payload as AccessTokenPayload;
          setUser({
            id: payload.sub || '',
            email: payload.email || '',
            name: payload.name || payload.email || '',
            roles: payload.roles || [],
          });
          setAccessToken(token);

          // Restore active profile from session storage
          const storedProfile = sessionStorage.getItem('activeProfile');
          if (storedProfile) {
            try {
              setActiveProfile(JSON.parse(storedProfile));
            } catch {
              // Ignore parse errors
            }
          }

          // Restore active tenant from session storage
          const storedTenant = sessionStorage.getItem('activeTenant');
          if (storedTenant) {
            try {
              setActiveTenant(JSON.parse(storedTenant));
            } catch {
              // Ignore parse errors
            }
          } else {
            // Check JWT for tenant claims (in case session was cleared)
            parseTenantClaims(decoded.payload as unknown as Record<string, unknown>);
          }
        }
      } catch (error) {
        console.error('Failed to check authentication:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const result = await authService.login({ email, password });
    
    if (!result.success) {
      throw new Error(result.error || 'Login failed');
    }
    
    if (result.user) {
      setUser({
        id: result.user.id,
        email: result.user.email,
        name: result.user.displayName || `${result.user.firstName} ${result.user.lastName}`.trim(),
        roles: result.user.roles || [],
      });
    }
    
    // Update access token after login
    const token = tokenService.getAccessToken();
    setAccessToken(token);

    // Handle profile selection
    if (result.requiresProfileSelection && result.profiles) {
      setRequiresProfileSelection(true);
      setPendingProfiles(result.profiles);
      return; // Don't navigate yet — profile selection needed
    }

    // Auto-selected profile (single profile)
    if (result.activeProfile) {
      setActiveProfile(result.activeProfile);
      sessionStorage.setItem('activeProfile', JSON.stringify(result.activeProfile));
    }

    setRequiresProfileSelection(false);
    setPendingProfiles([]);

    // Parse tenant claims from the newly issued JWT
    if (token) {
      try {
        const decoded = tokenService.decodeToken(token);
        parseTenantClaims(decoded.payload as unknown as Record<string, unknown>);
      } catch {
        // If JWT parsing fails, proceed without tenant context
      }
    }
  };

  const selectTenant = async (tenantId: string) => {
    const result = await tenantService.selectTenant(tenantId);

    if (!result.success) {
      throw new Error(result.error || 'Tenant selection failed');
    }

    // Find the selected tenant from pending list or stored memberships
    const storedMemberships = sessionStorage.getItem('tenantMemberships');
    const allMemberships = pendingTenants.length > 0
      ? pendingTenants
      : storedMemberships ? JSON.parse(storedMemberships) as TenantMembership[] : [];

    const selected = allMemberships.find((t) => t.tenantId === tenantId);
    const tenant: ActiveTenant = {
      tenantId,
      companyName: selected?.companyName,
      roles: result.roles || selected?.roles || [],
    };

    setActiveTenant(tenant);
    sessionStorage.setItem('activeTenant', JSON.stringify(tenant));
    setRequiresTenantSelection(false);
    setPendingTenants([]);

    // Update access token if a new one was issued
    if (result.accessToken) {
      setAccessToken(result.accessToken);
    }
  };

  const selectProfile = async (profileId: string) => {
    const result = await authService.selectProfile(profileId);

    if (!result.success) {
      throw new Error(result.error || 'Profile selection failed');
    }

    if (result.profile) {
      setActiveProfile(result.profile);
      sessionStorage.setItem('activeProfile', JSON.stringify(result.profile));
    }

    setRequiresProfileSelection(false);
    setPendingProfiles([]);
  };

  const logout = async () => {
    await authService.logout();
    tokenService.clearTokens();
    sessionStorage.removeItem('activeProfile');
    sessionStorage.removeItem('activeTenant');
    sessionStorage.removeItem('multiTenantUser');
    sessionStorage.removeItem('tenantMemberships');
    setUser(null);
    setAccessToken(null);
    setActiveProfile(null);
    setActiveTenant(null);
    setRequiresProfileSelection(false);
    setPendingProfiles([]);
    setRequiresTenantSelection(false);
    setPendingTenants([]);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    accessToken,
    requiresProfileSelection,
    pendingProfiles,
    activeProfile,
    requiresTenantSelection,
    pendingTenants,
    activeTenant,
    login,
    logout,
    selectProfile,
    selectTenant,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
